import React, { createContext, useContext, useEffect, useState } from 'react';
import { useWelcomeTour, WelcomeTourState, TourStep, TOUR_STEPS } from '@/hooks/useWelcomeTour';
import { TourSpotlight } from './TourSpotlight';
import { TourTooltip } from './TourTooltip';
import { WelcomeTourModal } from './WelcomeTourModal';
import { TourCompletionModal } from './TourCompletionModal';
import { useLocation } from 'react-router-dom';

interface WelcomeTourContextValue extends WelcomeTourState {
  currentStepData: TourStep;
  totalSteps: number;
  startTour: () => void;
  nextStep: () => Promise<void>;
  prevStep: () => Promise<void>;
  skipTour: () => Promise<void>;
  completeTour: () => Promise<void>;
  resetTour: () => Promise<void>;
  shouldStartTour: boolean;
}

const WelcomeTourContext = createContext<WelcomeTourContextValue | null>(null);

export function useWelcomeTourContext() {
  const context = useContext(WelcomeTourContext);
  if (!context) {
    throw new Error('useWelcomeTourContext must be used within WelcomeTourProvider');
  }
  return context;
}

interface WelcomeTourProviderProps {
  children: React.ReactNode;
}

export function WelcomeTourProvider({ children }: WelcomeTourProviderProps) {
  const tour = useWelcomeTour();
  const location = useLocation();
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  // Auto-start tour for new users
  useEffect(() => {
    if (tour.shouldStartTour && location.pathname === '/dashboard') {
      // Small delay to ensure page is rendered
      const timer = setTimeout(() => {
        tour.startTour();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [tour.shouldStartTour, location.pathname]);

  // Find target element when step changes
  useEffect(() => {
    if (!tour.isActive) {
      setTargetElement(null);
      return;
    }

    const step = tour.currentStepData;
    
    if (!step.elementId) {
      setTargetElement(null);
      return;
    }

    // Wait for navigation and element to render
    const findElement = () => {
      const element = document.getElementById(step.elementId!);
      if (element) {
        setTargetElement(element);
      } else {
        // Retry after a short delay
        setTimeout(findElement, 100);
      }
    };

    const timer = setTimeout(findElement, 300);
    return () => clearTimeout(timer);
  }, [tour.isActive, tour.currentStep, tour.currentStepData, location.pathname]);

  // Handle tour completion
  const handleComplete = async () => {
    await tour.completeTour();
    setShowCompletionModal(true);
  };

  // Check if current step is a modal step
  const isModalStep = tour.currentStepData.page === 'modal';
  const isFirstStep = tour.currentStep === 0;
  const isLastStep = tour.currentStep === TOUR_STEPS.length - 1;

  return (
    <WelcomeTourContext.Provider value={tour}>
      {children}

      {/* Welcome Modal (Step 0) */}
      {tour.isActive && isFirstStep && isModalStep && (
        <WelcomeTourModal
          onStart={tour.nextStep}
          onSkip={tour.skipTour}
        />
      )}

      {/* Spotlight + Tooltip for element-based steps */}
      {tour.isActive && !isModalStep && targetElement && (
        <>
          <TourSpotlight targetElement={targetElement} />
          <TourTooltip
            step={tour.currentStepData}
            currentStep={tour.currentStep}
            totalSteps={tour.totalSteps}
            targetElement={targetElement}
            onNext={isLastStep ? handleComplete : tour.nextStep}
            onPrev={tour.prevStep}
            onSkip={tour.skipTour}
            isFirst={tour.currentStep <= 1}
            isLast={isLastStep}
          />
        </>
      )}

      {/* Center modal for steps without element */}
      {tour.isActive && !isModalStep && !tour.currentStepData.elementId && (
        <TourTooltip
          step={tour.currentStepData}
          currentStep={tour.currentStep}
          totalSteps={tour.totalSteps}
          targetElement={null}
          onNext={isLastStep ? handleComplete : tour.nextStep}
          onPrev={tour.prevStep}
          onSkip={tour.skipTour}
          isFirst={tour.currentStep <= 1}
          isLast={isLastStep}
        />
      )}

      {/* Last step modal */}
      {tour.isActive && isLastStep && isModalStep && (
        <TourCompletionModal
          onClose={() => {
            tour.completeTour();
            setShowCompletionModal(false);
          }}
        />
      )}

      {/* Completion modal after tour ends */}
      {showCompletionModal && !tour.isActive && (
        <TourCompletionModal
          onClose={() => setShowCompletionModal(false)}
        />
      )}
    </WelcomeTourContext.Provider>
  );
}
